import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeneralService } from 'src/app/services/general/general.service';
import { PurchaseManagerService } from 'src/app/services/purchase-manager/purchase-manager.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';

declare var bootstrap: any;

@Component({
  selector: 'app-purchase-manager-upload-docs',
  templateUrl: './purchase-manager-upload-docs.component.html',
  styleUrls: ['./purchase-manager-upload-docs.component.scss']
})
export class PurchaseManagerUploadDocsComponent extends DestructorComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Output() documentsUploaded = new EventEmitter<void>();

  documentForm: FormGroup;
  selectedPurchaseManager: any = null;
  selectedFiles: File[] = [];
  isUploading: boolean = false;
  formSubmitted: boolean = false;
  uploadProgress: { current: number; total: number } = { current: 0, total: 0 };

  // Document types dropdown options for purchase manager
  documentTypes = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'purchase_order', label: 'Purchase Order' },
    { value: 'contract', label: 'Contract' },
    { value: 'payment_proof', label: 'Payment Proof' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'tax_document', label: 'Tax Document' },
    { value: 'shipping_document', label: 'Shipping Document' },
    { value: 'insurance', label: 'Insurance Document' },
    { value: 'warranty', label: 'Warranty Document' },
    { value: 'other', label: 'Other Documents' }
  ];

  constructor(
    private fb: FormBuilder,
    private _generalService: GeneralService,
    private _purchaseManagerService: PurchaseManagerService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    const modalElement = document.getElementById('kt_modal_purchase_manager_upload_docs');
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', () => {
        const selectedPM = (window as any).selectedPurchaseManagerForDocs;
        if (selectedPM) {
          this.setSelectedPurchaseManager(selectedPM);
        }
      });
    }
  }

  private initializeForm(): void {
    this.documentForm = this.fb.group({
      documentType: ['', [Validators.required]]
    });
  }

  setSelectedPurchaseManager(purchaseManager: any): void {
    this.selectedPurchaseManager = purchaseManager;
    if (!this.selectedPurchaseManager.pm_docs) {
      this.selectedPurchaseManager.pm_docs = {};
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async onSubmit(): Promise<void> {
    this.formSubmitted = true;

    if (this.documentForm.invalid || this.selectedFiles.length === 0) {
      return;
    }

    this.isUploading = true;
    this.uploadProgress = { current: 0, total: this.selectedFiles.length };

    try {
      const documentType = this.documentForm.get('documentType')?.value;
      const uploadedDocs: { name: string; url: string }[] = [];

      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        this.uploadProgress.current = i + 1;
        const fileUrl = await this.uploadSingleFile(file);
        uploadedDocs.push({
          name: file.name,
          url: fileUrl
        });
      }

      // Prepare pm_docs data structure
      const updatedPmDocs = { ...this.selectedPurchaseManager.pm_docs };

      if (updatedPmDocs[documentType]) {
        updatedPmDocs[documentType] = [...updatedPmDocs[documentType], ...uploadedDocs];
      } else {
        updatedPmDocs[documentType] = uploadedDocs;
      }

      // Log the data
      console.log('Purchase Manager Documents to submit:', {
        purchaseManagerId: this.selectedPurchaseManager._id,
        documentType: documentType,
        documentsUploaded: this.selectedFiles.length,
        pm_docs: updatedPmDocs
      });

      // Update using purchase manager service
      const updateData = { pm_docs: updatedPmDocs };

      this.subscriptions.push(
        this._purchaseManagerService.UpdateData(this.selectedPurchaseManager._id, updateData).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.selectedPurchaseManager.pm_docs = updatedPmDocs;
              console.log('Documents uploaded successfully!', updatedPmDocs);
              this.resetForm();
              this.documentsUploaded.emit();
              this.closeModal();
            } else {
              throw new Error(response.message || 'Failed to update purchase manager documents');
            }
          },
          error: (error) => {
            console.error('Error updating purchase manager documents:', error);
          }
        })
      );

    } catch (error) {
      console.error('Error uploading documents:', error);
    } finally {
      this.isUploading = false;
      this.uploadProgress = { current: 0, total: 0 };
    }
  }

  private uploadSingleFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
        next: (res) => {
          const fileUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

          this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
            next: () => {
              resolve(fileUrl);
            },
            error: (error) => {
              reject(error);
            }
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  getDocumentTypeLabel(value: string): string {
    const docType = this.documentTypes.find(type => type.value === value);
    return docType ? docType.label : value;
  }

  private resetForm(): void {
    this.documentForm.reset();
    this.selectedFiles = [];
    this.formSubmitted = false;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_purchase_manager_upload_docs');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  isImageFile(url: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const extension = this.getFileExtension(url).toLowerCase();
    return imageExtensions.includes('.' + extension);
  }

  getFileExtension(url: string): string {
    const parts = url.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  previewDocument(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  deleteDocument(docType: string, docIndex: number): void {
    if (confirm('Are you sure you want to delete this document?')) {
      const updatedPmDocs = { ...this.selectedPurchaseManager.pm_docs };

      if (updatedPmDocs[docType]) {
        updatedPmDocs[docType].splice(docIndex, 1);

        if (updatedPmDocs[docType].length === 0) {
          delete updatedPmDocs[docType];
        }

        const updateData = { pm_docs: updatedPmDocs };

        this.subscriptions.push(
          this._purchaseManagerService.UpdateData(this.selectedPurchaseManager._id, updateData).subscribe({
            next: (response: any) => {
              if (response.status) {
                this.selectedPurchaseManager.pm_docs = updatedPmDocs;
                console.log('Document deleted successfully');
                this.documentsUploaded.emit();
              } else {
                console.error('Failed to delete document:', response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting document:', error);
            }
          })
        );
      }
    }
  }

  getDocumentTypesFromPmDocs(): string[] {
    if (!this.selectedPurchaseManager?.pm_docs) {
      return [];
    }
    return Object.keys(this.selectedPurchaseManager.pm_docs);
  }

  getDocumentsForType(docType: string): any[] {
    if (!this.selectedPurchaseManager?.pm_docs || !this.selectedPurchaseManager.pm_docs[docType]) {
      return [];
    }
    return this.selectedPurchaseManager.pm_docs[docType];
  }

  getDocumentTypesCount(): number {
    return this.getDocumentTypesFromPmDocs().length;
  }
}
